/**
 * useProductComponents — ViewModel for /products/components, the
 * operator screen that declares which sub-products compose a given
 * parent product (migration 0117).
 *
 * Per SOP 02 §"MVVM Pattern". One fetch returns both the parent
 * picker (every product + its current components count) and — when a
 * productId is supplied — that parent's component list + the picker
 * for adding more.
 *
 * Mutations: add (POST), update (PATCH), delete (soft, DELETE). Each
 * one reloads the screen so the picker, list, and counts stay in
 * sync without per-row state juggling.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES } from '../constants/routes.constants'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'

export interface ParentPickerEntry {
  readonly id: string
  readonly name: string
  readonly active_component_count: number
}

export interface ComponentPickerEntry {
  readonly id: string
  readonly name: string
  /** True when picking this product would breach the depth-1 rule —
   *  it already has its own components. The UI greys these out. */
  readonly disabled: boolean
}

export interface ComponentRow {
  readonly parent_product_id: string
  readonly component_product_id: string
  readonly component_product_name: string
  readonly component_thumbnail_url: string | null
  readonly quantity: number
  readonly sort_order: number
  readonly created_at: string
  readonly updated_at: string
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly parents: ParentPickerEntry[]
    readonly parent?: { readonly id: string; readonly name: string; readonly is_already_component: boolean }
    readonly components: ComponentRow[]
    readonly picker: ComponentPickerEntry[]
  }
}

export interface UseProductComponentsReturn {
  readonly parents: ParentPickerEntry[]
  readonly selectedParentId: string | null
  readonly setSelectedParentId: (id: string | null) => void
  readonly parentMeta: { id: string; name: string; is_already_component: boolean } | null
  readonly components: ComponentRow[]
  readonly picker: ComponentPickerEntry[]
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
  readonly addComponent: (input: {
    componentProductId: string
    quantity: number
    sortOrder: number
  }) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly updateComponent: (input: {
    componentProductId: string
    quantity?: number
    sortOrder?: number
  }) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly deleteComponent: (componentProductId: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

export function useProductComponents(): UseProductComponentsReturn {
  const { authedFetch } = useAuth()
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null)
  const [parents, setParents] = useState<ParentPickerEntry[]>([])
  const [parentMeta, setParentMeta] = useState<{ id: string; name: string; is_already_component: boolean } | null>(null)
  const [components, setComponents] = useState<ComponentRow[]>([])
  const [picker, setPicker] = useState<ComponentPickerEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadTick, setReloadTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    const ctrl = new AbortController()
    setIsLoading(true)
    setError(null)
    void (async () => {
      try {
        const qs = selectedParentId !== null && selectedParentId !== ''
          ? `?productId=${encodeURIComponent(selectedParentId)}`
          : ''
        const url = `${APIROUTES.PRODUCTCOMPONENTSAPI}${qs}`
        const res = await authedFetch(url, { signal: ctrl.signal })
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as ListBody
        if (cancelled) return
        setParents(body.data.parents)
        setParentMeta(body.data.parent ?? null)
        setComponents(body.data.components)
        setPicker(body.data.picker)
      } catch (err) {
        if (cancelled) return
        if (err instanceof DOMException && err.name === 'AbortError') return
        setError(err instanceof Error ? err.message : 'Failed to load')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [authedFetch, selectedParentId, reloadTick])

  const reload = useCallback(() => setReloadTick((n) => n + 1), [])

  const addComponent = useCallback(
    async (input: { componentProductId: string; quantity: number; sortOrder: number }) => {
      if (selectedParentId === null || selectedParentId === '') {
        return { ok: false as const, message: 'No parent product selected' }
      }
      const r = await mutateJson<{ parent_product_id: string }>(
        authedFetch,
        APIROUTES.PRODUCTCOMPONENTSAPI,
        'POST',
        {
          parentProductId: selectedParentId,
          componentProductId: input.componentProductId,
          quantity: input.quantity,
          sortOrder: input.sortOrder
        }
      )
      if (r.ok) {
        reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, selectedParentId, reload]
  )

  const updateComponent = useCallback(
    async (input: { componentProductId: string; quantity?: number; sortOrder?: number }) => {
      if (selectedParentId === null || selectedParentId === '') {
        return { ok: false as const, message: 'No parent product selected' }
      }
      const body: { quantity?: number; sortOrder?: number } = {}
      if (input.quantity !== undefined) body.quantity = input.quantity
      if (input.sortOrder !== undefined) body.sortOrder = input.sortOrder
      const path = `${APIROUTES.PRODUCTCOMPONENTSAPI}/${encodeURIComponent(selectedParentId)}/${encodeURIComponent(input.componentProductId)}`
      const r = await mutateJson<{ parent_product_id: string }>(authedFetch, path, 'PATCH', body)
      if (r.ok) {
        reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, selectedParentId, reload]
  )

  const deleteComponent = useCallback(
    async (componentProductId: string) => {
      if (selectedParentId === null || selectedParentId === '') {
        return { ok: false as const, message: 'No parent product selected' }
      }
      const path = `${APIROUTES.PRODUCTCOMPONENTSAPI}/${encodeURIComponent(selectedParentId)}/${encodeURIComponent(componentProductId)}`
      const r = await mutateJson<{ ok: true }>(authedFetch, path, 'DELETE')
      if (r.ok) {
        reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, selectedParentId, reload]
  )

  return {
    parents,
    selectedParentId,
    setSelectedParentId,
    parentMeta,
    components,
    picker,
    isLoading,
    error,
    reload,
    addComponent,
    updateComponent,
    deleteComponent
  }
}
